import React from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { useT } from "@/lib/LanguageContext";
import useNotificationSettings from "./useNotificationSettings";
// Relative, not "@/...": the validation infrastructure is imported for real under Jest
// (no "@" alias there) — same reasoning as lib/api.js requiring "./apiErrors".
import useFieldErrors from "../../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import { isBlankOrValidEmail } from "../../lib/validation";

// A blank recipient is the contract's own "use the account default" (useNotificationSettings
// normalises it to `null` before the PUT); only a NON-blank value has to look like an email.
// The message key is `settings.notif.invalidEmail` — the SAME one the 422 branch below
// already shows for the identical fact, reported by the server instead of caught here first.
//
// `isBlankOrValidEmail` (lib/validation.js) is the shared, trim-first predicate — deliberately
// NOT lib/validation.js's own `optionalEmailField`, whose blank branch is an exact `z.literal
// ("")` and so REJECTS a whitespace-only value (it falls through to a required-email check).
// Here, whitespace-only is deliberately treated as BLANK (allowed): useNotificationSettings's
// own save() trims the recipient and writes `null` for anything that trims to "" — validation
// and persistence have to agree on what "blank" means, or a value the wire layer silently
// normalises away could still block the very save that normalises it.
const NOTIF_SCHEMA = z.object({ recipient_email: z.string() }).superRefine((val, ctx) => {
  if (!isBlankOrValidEmail(val.recipient_email)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["recipient_email"], message: "settings.notif.invalidEmail" });
  }
});

/**
 * Notification preferences card rendered inside SettingsAdmin. Fixed data-testids per
 * docs/auth-and-admin/notification-settings.md — tests are written against them.
 */
export default function NotificationSettingsCard() {
  const t = useT("admin");
  const tc = useT("common");
  const { settings, setField, save, busy, error, saved } = useNotificationSettings();
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  const handleSave = () => {
    if (validate(NOTIF_SCHEMA, { recipient_email: settings.recipient_email || "" })) {
      toast.error(tc("validation.fixHighlighted"));
      return;
    }
    save();
  };

  return (
    <div
      data-testid="notif-settings-card"
      className="mt-8 bg-eh-cream border border-eh-border-soft rounded-3xl p-7 space-y-5 max-w-3xl"
    >
      <div>
        <h2 className="font-display text-2xl text-eh-ink">{t("settings.notif.title")}</h2>
        <p className="text-eh-muted text-sm mt-1">{t("settings.notif.subtitle")}</p>
      </div>

      {error && (
        <p data-testid="notif-error" className="text-sm text-red-700">
          {error}
        </p>
      )}

      {!settings ? (
        !error && <Loader2 className="animate-spin text-eh-muted" size={18} />
      ) : (
        <>
          <ToggleRow
            testId="notif-master-toggle"
            label={t("settings.notif.master")}
            checked={!!settings.enabled}
            onChange={(v) => setField("enabled", v)}
          />

          <div className="pl-7 space-y-4">
            <ToggleRow
              testId="notif-booking-toggle"
              label={t("settings.notif.booking")}
              hint={t("settings.notif.bookingHint")}
              checked={!!settings.booking}
              disabled={!settings.enabled}
              onChange={(v) => setField("booking", v)}
            />
            <ToggleRow
              testId="notif-purchase-toggle"
              label={t("settings.notif.purchase")}
              hint={t("settings.notif.purchaseHint")}
              checked={!!settings.purchase}
              disabled={!settings.enabled}
              onChange={(v) => setField("purchase", v)}
            />
            {/* One notice per unread EPISODE, not per message — the claim is made in
                modules/messaging/side_effects.py, and the hint says so, because a
                practitioner who expects one email per message will read silence as a
                failure. */}
            <ToggleRow
              testId="notif-messages-toggle"
              label={t("settings.notif.messages")}
              hint={t("settings.notif.messagesHint")}
              checked={!!settings.messages}
              disabled={!settings.enabled}
              onChange={(v) => setField("messages", v)}
            />
          </div>

          {/* The recipient stays editable even with the master switch off: operational
              alerts (needs-attention, calendar drift) always send and honour this
              override — see the gate-semantics table in the contract doc. */}
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-eh-muted block mb-1">
              {t("settings.notif.recipient")}
            </label>
            <input
              type="email"
              data-testid="notif-recipient-input"
              value={settings.recipient_email || ""}
              onChange={(e) => { setField("recipient_email", e.target.value); clearFieldError("recipient_email"); }}
              placeholder={t("settings.notif.recipientHint")}
              className={`w-full bg-white border rounded-xl px-3 py-2 text-sm text-eh-ink outline-none focus:border-eh-sage-deep ${fieldErrors.recipient_email ? invalidInputClass : "border-eh-border-soft"}`}
            />
            <FieldError
              message={fieldErrors.recipient_email ? t(fieldErrors.recipient_email) : ""}
              testId="notif-recipient-input-error"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="notif-save"
              onClick={handleSave}
              disabled={busy}
              className="btn-primary text-sm"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}{" "}
              {t("settings.notif.save")}
            </button>
            {saved && <span className="text-sm text-eh-sage-deep">✓ {t("settings.notif.saved")}</span>}
          </div>
        </>
      )}
    </div>
  );
}

const ToggleRow = ({ testId, label, hint, checked, disabled, onChange }) => (
  <label
    className={`flex items-start gap-3 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <input
      type="checkbox"
      data-testid={testId}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 w-4 h-4 accent-eh-sage-deep"
    />
    <span>
      <span className="text-sm text-eh-ink block">{label}</span>
      {hint && <span className="text-xs text-eh-muted block mt-0.5">{hint}</span>}
    </span>
  </label>
);
